import { useState, useMemo } from 'react';
import { Typography, Grid } from '@mui/material';
import { useQueryClient } from 'react-query';
import PropTypes from 'prop-types';

// Our components
import Alert from 'components/Alert';
import { PrimaryButton } from 'components/Button/Button';
import CurrencyTextInput from 'components/Input/CurrencyTextInput';
import Dropdown from 'components/Dropdown/Dropdown';
import GetFullAddressPartialForm from 'components/Forms/addressRelated/GetFullAddressPartialForm';
import Input from 'components/Input/TextInput';
import Loader from 'components/Loader';
import LoanInquirySuccessModal from 'components/Modal/LoanInquiryConfirmationModal';
import PropertyTypeDropdown from 'components/Dropdown/mortgage/PropertyTypeDropdown';
import RateInput from 'components/Input/RateInput';
import RequiredFieldsTooltip from 'components/Forms/NewLoanForms/RequiredFieldsTooltip';
import SoraTextField from 'components/Input/SoraTextField';

// Form Input Types
import { NUMBER } from 'components/Input/Types';

// Our Query Keys
import { ERROR_MESSAGE_DATA } from 'shared/query-keys';

// Our hooks 🪝
import useMutateCreateDeal from 'hooks/hubspot/useMutateCreateDeal';
import useMutateSaveNewLoanInquiry from 'hooks/newLoanInquiries/useMutateSaveNewLoanInquiry';
import useMutateUpdateLoanInquiry from 'hooks/newLoanInquiries/useMutateUpdatedLoanInquiry';
import useMutateSendGenericEmail from 'hooks/emails/useMutateSendGenericEmail';
import usePrepopulateState from 'hooks/prepopulate/usePrepopulateState';

// utils
import {
	clearFormValues,
	dollarFormatter,
	isSubmissionReady
} from 'shared/utils';
import createDefaultCloseDate from 'shared/utils/hubspot/createDefaultCloseDate';
import formatCollection from 'shared/utils/formatting/currency/formatCollection';
import getDwellingType from 'shared/utils/mortgage/getDwellingType';
import getPropertyUse from 'shared/utils/mortgage/getPropertyUse';

// Our emails
import lendingEmailTemplate from 'emails/lendingEmailTemplate';

// Prop Types
import {
	advisorInfoProps,
	clientInfoProps,
	financialDataProps,
	lendingPropTypes,
	mortgagePropTypes
} from 'shared/propTypes/lendingPropTypes';

// Constants
import {
	MORTGAGE,
	MORTGAGE_LOAN_TENURE_OPTIONS,
	REFINANCE_OFFER_TYPE,
	TYPES_OF_HOME_PROPERTIES
} from 'shared/constants';

function MortgageRefinanceForm({
	advisorInfo,
	clientInfo,
	isClientNameProvided,
	loanType,
	loanRequestId,
	financialData,
	clientMortgageInfo,
	postSuccessEvent,
	disableAutoComplete,
	setDisableAutoComplete
}) {
	const queryClient = useQueryClient();
	const currentURL = window.location.href;
	const atLoanRequestDetailsUrl = currentURL.includes('loan-request-details');

	const updateLoanInquiry = useMutateUpdateLoanInquiry();

	// API Calls
	const saveLoanInquiry = useMutateSaveNewLoanInquiry();
	const { isLoading: savingLoanInquiry } = saveLoanInquiry;

	// Mutations
	const sendGenericEmail = useMutateSendGenericEmail();
	const createDeal = useMutateCreateDeal();

	// Build full name
	const {
		clientFullName,
		clientId,
		firstName,
		lastName,
		annualIncome,
		creditScoreValue
	} = clientInfo;

	const { advisorName, advisorEmail, advisorCompany, advisorWebsite } =
		advisorInfo;

	const {
		currentLoanInterestRateValue,
		currentOutstandingBalance,
		currentLoanRemainingTermValue,
		refinancingTradelineId
	} = financialData;

	const {
		addressLine1Value,
		addressLine2Value,
		cityValue,
		dwellingType,
		estimatedValue,
		primaryUseValue,
		stateValue,
		zipCodeValue
	} = clientMortgageInfo;

	const [estimatedPurchaseValue, setEstimatedPurchaseValue] = useState('');
	const [loanAmount, setLoanAmount] = useState('');
	const [currentInterestRate, setCurrentInterestRate] = useState('');
	const [tradelineId, setTradelineId] = useState('');
	const [primaryUse, setPrimaryUse] = useState('');
	const [propertyType, setPropertyType] = useState('');

	const [remainingTerm, setRemainingTerm] = useState('');

	// Address related fields
	const [addressLine1, setAddressLine1] = useState('');
	const [addressLine2, setAddressLine2] = useState('');
	const [city, setCity] = useState('');
	const [state, setState] = useState('');
	const [zipCode, setZipCode] = useState('');

	const [ownershipTenure, setOwnershipTenure] = useState('');

	const [additionalNotes, setAdditionalNotes] = useState('');
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	const formData = [
		propertyType,
		primaryUse,
		loanAmount,
		currentInterestRate,
		ownershipTenure,
		city,
		state,
		zipCode
	];

	const loanRequestDetailsRequiredFields = [
		propertyType,
		primaryUse,
		loanAmount,
		currentInterestRate,
		ownershipTenure,
		city,
		state,
		zipCode,
		remainingTerm
	];

	const isFormReady = useMemo(
		() =>
			isSubmissionReady(
				atLoanRequestDetailsUrl
					? loanRequestDetailsRequiredFields
					: formData
			) && isClientNameProvided,
		atLoanRequestDetailsUrl
			? loanRequestDetailsRequiredFields
			: [...formData, isClientNameProvided]
	);

	usePrepopulateState(
		[
			{
				setState: setPrimaryUse,
				value: getPropertyUse(primaryUseValue)
			},
			{
				setState: setEstimatedPurchaseValue,
				value: estimatedValue
			},
			{
				setState: setCity,
				value: cityValue
			},
			{
				setState,
				value: `${stateValue}`
			},
			{
				setState: setTradelineId,
				value: refinancingTradelineId
			},
			{
				setState: setAddressLine1,
				value: addressLine1Value
			},
			{
				setState: setAddressLine2,
				value: addressLine2Value
			},
			{
				setState: setZipCode,
				value: zipCodeValue
			},
			{
				setState: setCurrentInterestRate,
				value: `${currentLoanInterestRateValue}`
			},
			{
				setState: setLoanAmount,
				value: `${currentOutstandingBalance}`
			},
			{
				setState: setRemainingTerm,
				value: Math.floor(currentLoanRemainingTermValue / 12)
			},
			{
				setState: setPropertyType,
				value: getDwellingType(dwellingType)
			}
		],

		[
			currentLoanRemainingTermValue,
			cityValue,
			refinancingTradelineId,
			currentOutstandingBalance,
			currentLoanInterestRateValue,
			estimatedValue,
			primaryUseValue,
			stateValue,
			zipCodeValue,
			addressLine1Value,
			addressLine2Value,
			dwellingType
		]
	);

	const sendFormData = () => {
		const sendToArray = ['lending@sorafinance.com'];
		const subject = `Mortgage Loan Refinance Request - ${clientFullName}`;

		const emailTemplate = lendingEmailTemplate({
			advisorInfo,
			clientInfo,
			financialData,
			clientFullName,
			loanAmount,
			loanRelatedDetails: [
				`Client's Current Mortgage Interest Rate: ${currentInterestRate}`,
				`Client's remaining term of current loan ${remainingTerm}`,
				`Client's expected ownership tenure: ${ownershipTenure}`,
				`Estimated purchase value of home being refinanced: ${dollarFormatter.format(
					+estimatedPurchaseValue
				)}`,
				`Primary use of property: ${primaryUse}`,
				`Property type: ${propertyType}`,
				`Property address: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}`
			],
			additionalNotes,
			lendingTypeRequested: 'Mortgage Loan Refinance'
		});

		sendGenericEmail.mutate(
			{
				firstName,
				lastName,
				subject,
				message: emailTemplate,
				sendToArray
			},
			{
				onSuccess: () => {
					clearFormValues([
						setAdditionalNotes,
						setLoanAmount,
						setCity,
						setAddressLine1,
						setAddressLine2,
						setEstimatedPurchaseValue,
						setRemainingTerm,
						setOwnershipTenure,
						setPrimaryUse,
						setPropertyType,
						setCurrentInterestRate,
						setState,
						setZipCode
					]);
				}
			}
		);
	};

	const submitNewLoanInquiry = () => {
		const newLoanInquiryData = {
			additionalNotes,
			addressLine1,
			addressLine2,
			city,
			clientId,
			currentInterestRate: +currentInterestRate,
			estimatedPurchaseValue,
			firstName,
			lastName,
			loanAmount,
			offerType: REFINANCE_OFFER_TYPE,
			ownershipTenure,
			primaryUse,
			propertyType,
			remainingTerm,
			state,
			tradelineId,
			tradelineType: MORTGAGE,
			zipCode
		};

		// Call mutation to update user's data
		saveLoanInquiry.mutate(newLoanInquiryData, {
			onSuccess: () => {
				setIsSuccessModalOpen(true);
			},
			onError: () => {
				// Set error message
				queryClient.setQueryData(
					ERROR_MESSAGE_DATA,
					'There was an error making your loan request.'
				);
			}
		});
	};

	const updateLoanRequest = () => {
		const updatedLoanInquiryData = {
			additionalNotes,
			addressLine1,
			addressLine2,
			city,
			clientId,
			estimatedPurchaseValue,
			remainingTerm,
			firstName,
			lastName,
			loanAmount,
			offerType: REFINANCE_OFFER_TYPE,
			ownershipTenure,
			primaryUse,
			propertyType,
			currentInterestRate,
			state,
			tradelineType: MORTGAGE,
			zipCode
		};

		// Call mutation to update user's data
		updateLoanInquiry.mutate(
			{ loanRequestId, updatedLoanInquiryData },
			{
				onSuccess: () => {
					clearFormValues([
						setAdditionalNotes,
						setAddressLine1,
						setAddressLine2,
						setLoanAmount,
						setCity,
						setEstimatedPurchaseValue,
						setRemainingTerm,
						setOwnershipTenure,
						setPrimaryUse,
						setPropertyType,
						setCurrentInterestRate,
						setState,
						setZipCode
					]);

					if (postSuccessEvent) {
						postSuccessEvent();
					}
				},
				onError: () => {
					// Set error message
					queryClient.setQueryData(
						ERROR_MESSAGE_DATA,
						'There was an error making your loan request.'
					);
				}
			}
		);
	};

	const { isLoading } = sendFormData;

	const submitForm = (event) => {
		event.preventDefault();

		const closeDate = createDefaultCloseDate();
		const valuesToFormat = [
			{
				type: 'currency',
				value: annualIncome,
				label: 'Annual Income:'
			},
			{
				type: 'percent',
				value: currentInterestRate,
				label: 'Current mortgage interest rate:'
			},
			{
				type: 'currency',
				value: loanAmount,
				label: 'Current mortgage outstanding balance:'
			},
			{
				type: 'currency',
				value: estimatedPurchaseValue,
				label: 'Estimated Purchase Value:'
			}
		];

		const formattedCollection = formatCollection(valuesToFormat);

		const advisorNotes = `Advisor's name: ${advisorName}
				Advisor's email: ${advisorEmail}
				Advisor's company: ${advisorCompany}
				Credit Score: ${creditScoreValue}
				${formattedCollection}
				Property Type: ${propertyType}
				Primary Use: ${primaryUse}
				Remaining loan term: ${remainingTerm}
				Desired loan term: ${ownershipTenure}
				Address: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}
				------------------------------------------------------------------------------------------------
				Advisor's notes: ${additionalNotes}`;

		createDeal.mutate({
			advisorInfo: {
				advisorName,
				email: advisorEmail,
				companyName: advisorCompany,
				companyDomain: advisorWebsite
			},
			properties: {
				amount: loanAmount,
				dealname: `${advisorName} / ${clientFullName}`,
				dealtype: loanType,
				borrower: clientFullName,
				closedate: closeDate
			},
			noteInfo: {
				hs_note_body: advisorNotes
			}
		});

		// If we're at /loan-inquiry, perform the following
		if (!atLoanRequestDetailsUrl) {
			submitNewLoanInquiry();
			sendFormData();
		}
		// Else if we're at /loan-inquiry-details, perform the following
		else if (atLoanRequestDetailsUrl) {
			// Call new mutations here patching the new loan inquiry
			updateLoanRequest(loanRequestId);
		}
	};

	// Handlers for mutation
	const { isError: sendFormDataError } = sendFormData;

	return (
		<>
			{!atLoanRequestDetailsUrl && (
				<Grid item xs={12}>
					<Typography variant="h2Gascogne">
						Mortgage Loan Refinance
					</Typography>

					<Typography variant="body2">
						Fill in the information below to submit a loan request
						to Sora. You will be able to complete and review your
						loan request in the dashboard task bar.
					</Typography>
				</Grid>
			)}

			{isLoading || (savingLoanInquiry && <Loader />)}
			{!isLoading && !savingLoanInquiry && (
				<>
					{sendFormDataError && <Alert variant="error" />}

					{/* Modal shown on successful submission of  */}
					<LoanInquirySuccessModal isOpen={isSuccessModalOpen} />

					<Grid item xs={12} md={6}>
						{/* Type of property input */}
						<PropertyTypeDropdown
							selected={propertyType}
							onChange={setPropertyType}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Primary use of property */}
						<Dropdown
							items={TYPES_OF_HOME_PROPERTIES}
							selected={primaryUse}
							onChange={setPrimaryUse}
							variant="outlined"
							label="Property use *"
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Outstanding Mortgage Balance */}
						<CurrencyTextInput
							label="Current mortgage outstanding balance *"
							value={loanAmount}
							onChange={setLoanAmount}
							inputProps={{
								'data-test': 'outstandingBalanceCurrentMortgage'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Current Loan Interest Rate */}
						<RateInput
							rate={currentInterestRate}
							setRate={setCurrentInterestRate}
							label="Current mortgage interest rate *"
							inputProps={{
								'data-test': 'interestRateCurrentMortgage'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Current Loan Remaining term */}
						<Input
							type={NUMBER}
							label={
								atLoanRequestDetailsUrl
									? 'Remaining loan term *'
									: 'Remaining loan term'
							}
							subLabel="Length of loan in years"
							onChange={setRemainingTerm}
							value={remainingTerm}
							inputProps={{
								'data-test': 'Desired loan term'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Loan Tenure */}
						<Dropdown
							items={MORTGAGE_LOAN_TENURE_OPTIONS}
							selected={ownershipTenure}
							onChange={setOwnershipTenure}
							variant="outlined"
							label="Desired loan term *"
						/>
					</Grid>

					<GetFullAddressPartialForm
						isRequired
						addressLine1={addressLine1}
						addressLine2={addressLine2}
						city={city}
						state={state}
						zipCode={zipCode}
						setAddressLine1={setAddressLine1}
						setAddressLine2={setAddressLine2}
						setCity={setCity}
						setState={setState}
						setZipCode={setZipCode}
						disableAutoComplete={disableAutoComplete}
						setDisableAutoComplete={setDisableAutoComplete}
					/>

					<Grid item xs={12}>
						<SoraTextField
							label="Additional Notes"
							value={additionalNotes}
							onChange={setAdditionalNotes}
						/>
					</Grid>
				</>
			)}
			<Grid item xs={12}>
				{!isFormReady && (
					<RequiredFieldsTooltip>
						<span>
							<PrimaryButton
								disabled={!isFormReady}
								onClick={submitForm}
							>
								Submit
							</PrimaryButton>
						</span>
					</RequiredFieldsTooltip>
				)}
				{isFormReady && (
					<PrimaryButton onClick={submitForm}>Submit</PrimaryButton>
				)}
			</Grid>
		</>
	);
}

MortgageRefinanceForm.propTypes = {
	...lendingPropTypes,
	...mortgagePropTypes,
	advisorInfo: advisorInfoProps.isRequired,
	clientInfo: clientInfoProps.isRequired,
	financialData: financialDataProps.isRequired,
	disableAutoComplete: PropTypes.bool,
	setDisableAutoComplete: PropTypes.func
};

MortgageRefinanceForm.defaultProps = {
	disableAutoComplete: false,
	setDisableAutoComplete: () => {}
};

export default MortgageRefinanceForm;
